import type { AppFeatures, NonUserAppFeatures } from "../components/SerializedApp";
import type { QuotaLimitList } from "./quotas";
import { QuotaKind, defaultQuotaLimits } from "./quotas";
import type { GrandfatheringFeatures } from ".";

// `true` means supported.
// `"upgrade"` means not supported, but upgrading adds support.
// `false` means not supported, and no way to get support.
export type SupportLevel = boolean | "upgrade";

export interface AuthenticationSupport {
    readonly public: SupportLevel;
    readonly publicWithEmail: SupportLevel;
    readonly publicDisabled: SupportLevel;
    readonly password: SupportLevel;
    readonly emailWhitelist: SupportLevel;
    readonly organizationMembers: SupportLevel;
    readonly justMe: SupportLevel;
    readonly userProfiles: SupportLevel;

    // Can Public with Email be made optional?
    readonly optionalPublicWithEmail: SupportLevel;
    // Can the other Email Pin auth methods be made optional?
    readonly optionalOtherEmail: SupportLevel;
}

export type TransferToOrgAs = "internal" | "public" | "free" | undefined;

export type StripeDowngradeTime = "immediately" | undefined;

export interface EminenceFlagsBase {
    readonly isFreeEminence: boolean;
    // Some legacy features must still be supported (until we promote existing legacy pro subscriptions to a newer
    // pricing model), so we need a flag to verify this.
    readonly isV1Pro: boolean;
    readonly base64EncodePublicUserProfiles: boolean;
    readonly needsBuilderModifyAppFeatures: boolean;
    readonly removeBranding: boolean;
    readonly inYourFaceBranding: boolean;
    readonly lowTransactionFees: boolean;
    readonly googleSharedDrive: boolean;
    readonly customDomains: boolean;
    readonly badge: string | undefined;
    readonly withGrandfathering: boolean;
    readonly automaticRefresh: boolean;
    readonly googleAnalytics: boolean;
    readonly unlimitedZapier: boolean;
    readonly signInAgreements: boolean;
    readonly customSignInAppearance: boolean;
    readonly authenticationSupport: AuthenticationSupport;
    readonly upgradeButton: boolean;
    readonly proComponents: boolean;
    readonly proIcons: boolean;
    readonly isUserMetered: boolean;
    readonly canDisableShare: boolean;
    // Org apps don't support the Buy Button yet
    readonly buyButton: boolean;
    readonly canUpgrade: boolean;
    readonly canDowngrade: boolean;
    // Can this app be transferred?  NOTE: If we want to
    // allow this for non-free apps, we will also have to implement
    // all the Pro bookkeeping.
    readonly canTransferBetweenUsers: boolean;
    readonly canTransferToOrg: boolean;
    readonly canUnpublish: boolean;
    readonly canPausePublish: boolean;
    readonly canUseSignInWithGoogle: boolean;
    // Everything can be deleted, but we sometimes need to downgrade
    // apps before deletion.
    readonly downgradeBeforeDelete: boolean;
    readonly downgradeBeforeDeleteStripeCancellation: StripeDowngradeTime;
    readonly logOrgLogin: boolean;
    readonly allowUsersToCopyApp: boolean;
    readonly canSubmitTemplate: boolean;
    readonly needsPublishableOrgToTransfer: boolean;
    readonly transferToOrgAs: TransferToOrgAs;
    readonly showQuotaBanners: boolean;
    readonly unlimitedPasswordAndTeamMembersOnlyUsers: boolean;
    readonly proDisplayQuotas: boolean;
    readonly canHaveBoosts: boolean;
    readonly tabletMode: boolean;
    readonly showReportApp: boolean;
    readonly confirmChangeAuthOnUpgrade: boolean;
    readonly roles: boolean;
    readonly protectedColumns: boolean;
    readonly allowRealEmails: boolean;
    readonly allowVirtualEmails: boolean;
    readonly stripeTransactionFeePercentage: number;
    readonly canChangeAuthType: boolean;
    readonly isPrivateProTrial: boolean;
    readonly appLoginsSheet: boolean;
    // via referrals and boosts
    readonly supportsQuotaUpgrades: boolean;
    readonly maxSingleUploadBytes: number;
    readonly quotas: QuotaLimitList;
}

// FIXME: Remove this once we're fully deployed on the new billing code
export enum Eminence {
    Free = "free",
    OrgFree = "org-free",
    OrgFreePage = "org-free-page",
    Pro = "pro",
    OrgUnpublished = "org-unpublished",
    OrgInternal = "org-internal",
    OrgPublic = "org-public",
    UnifiedBasic = "unified-basic",
    UnifiedPro = "unified-pro",
    UnifiedProPrivate = "unified-private",
    UnifiedProPrivateTrial = "unified-private-trial",
    UnifiedBasicPrivate = "unified-basic-private",
    OrgUnifiedProPrivate = "org-unified-pro-private",
    OrgUnifiedProPrivatePage = "org-unified-pro-private-page",
    OrgUnifiedBasicPrivate = "org-unified-basic-private",
}

export interface EminenceFlags extends EminenceFlagsBase {
    // FIXME: remove `eminence` once we're fully deployed on the new billing
    // code.
    readonly eminence: Eminence;

    readonly displayName: string;
}

export type EminencesByApp = Record<string, EminenceFlags>;

// NOTE: If you, like I, are tempted to replace this with an elegant `Infinity`, please don't.
// `Infinity` is not part of the JSON spec (despite being valid in IEEE!), and will serialize weirdly.
// For most math-related purposes, MAX_SAFE_INTEGER behaves like infinity, which is nice for quotas.
export const maxQuotaNumber = Number.MAX_SAFE_INTEGER;

const proEminenceFlags = {
    isFreeEminence: false,
    isV1Pro: true,
    confirmChangeAuthOnUpgrade: true,
    needsBuilderModifyAppFeatures: false,
    removeBranding: true,
    inYourFaceBranding: false,
    lowTransactionFees: true,
    googleSharedDrive: true,
    customDomains: true,
    withGrandfathering: false,
    automaticRefresh: true,
    googleAnalytics: true,
    isUserMetered: false,
    unlimitedZapier: true,
    signInAgreements: true,
    customSignInAppearance: true,
    proComponents: true,
    proIcons: true,
    canDisableShare: true,
    canTransferBetweenUsers: false,
    canUnpublish: false,
    canPausePublish: true,
    canUseSignInWithGoogle: false,
    downgradeBeforeDeleteStripeCancellation: undefined,
    buyButton: true,
    showQuotaBanners: false,
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    proDisplayQuotas: true,
    tabletMode: true,
    showReportApp: false,
    protectedColumns: true,
    allowRealEmails: true,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 2,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    appLoginsSheet: true,
    supportsQuotaUpgrades: true,
    maxSingleUploadBytes: 1024 * 1024 * 1024,
    quotas: {
        ...defaultQuotaLimits,
        [QuotaKind.BarcodesScanned]: { prepaidUsage: 1000, maxOverage: maxQuotaNumber },
        [QuotaKind.FileBytesUsed]: { prepaidUsage: 10 * 1000 * 1000 * 1000, maxOverage: maxQuotaNumber },
        [QuotaKind.Geocodes]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.PrivateUsers]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.Reloads]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.RowsUsed]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.ExcelRowsUsed]: { prepaidUsage: 10, maxOverage: 0 },
        [QuotaKind.Signatures]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.ZapsAndWebhook]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.MutateTablesAPI]: { prepaidUsage: 10000, maxOverage: 0 },
    },
};

export const freeFlags: EminenceFlags = {
    eminence: Eminence.Free,

    isFreeEminence: true,
    isV1Pro: false,
    displayName: "Personal App",
    needsBuilderModifyAppFeatures: true,
    removeBranding: false,
    inYourFaceBranding: true,
    lowTransactionFees: false,
    googleSharedDrive: false,
    confirmChangeAuthOnUpgrade: true,
    customDomains: false,
    badge: undefined,
    isUserMetered: false,
    withGrandfathering: true,
    automaticRefresh: false,
    googleAnalytics: false,
    unlimitedZapier: false,
    signInAgreements: false,
    customSignInAppearance: false,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: true,
        emailWhitelist: "upgrade",
        userProfiles: "upgrade",
        organizationMembers: false,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    upgradeButton: true,
    proComponents: false,
    proIcons: false,
    canDisableShare: false,
    buyButton: true,
    canUpgrade: true,
    canDowngrade: false,
    canTransferBetweenUsers: true,
    canTransferToOrg: true,
    canUnpublish: false,
    canPausePublish: false,
    canUseSignInWithGoogle: true,
    downgradeBeforeDelete: false,
    downgradeBeforeDeleteStripeCancellation: undefined,
    logOrgLogin: false,
    allowUsersToCopyApp: true,
    canSubmitTemplate: true,
    needsPublishableOrgToTransfer: false,
    transferToOrgAs: "free",
    showQuotaBanners: true,
    unlimitedPasswordAndTeamMembersOnlyUsers: false,
    proDisplayQuotas: false,
    canHaveBoosts: true,
    tabletMode: false,
    showReportApp: true,
    roles: false,
    protectedColumns: false,
    allowRealEmails: false,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 10,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    appLoginsSheet: true,
    supportsQuotaUpgrades: true,
    maxSingleUploadBytes: 10 * 1024 * 1024,
    quotas: defaultQuotaLimits,
};

const orgFreeFlags = {
    ...freeFlags,
    eminence: Eminence.OrgFree,
    authenticationSupport: {
        ...freeFlags.authenticationSupport,
        justMe: false,
        organizationMembers: true,
    },
    supportsQuotaUpgrades: true,
};

export const freePageFlags: EminenceFlags = {
    ...orgFreeFlags,
    eminence: Eminence.OrgFreePage,
    displayName: "Personal Page",
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    allowRealEmails: true,
    allowVirtualEmails: false,
    authenticationSupport: {
        public: false,
        publicWithEmail: "upgrade",
        publicDisabled: true,
        password: false,
        emailWhitelist: false,
        userProfiles: "upgrade",
        organizationMembers: true,
        justMe: false,
        optionalPublicWithEmail: "upgrade",
        optionalOtherEmail: "upgrade",
    },
    appLoginsSheet: false,
    supportsQuotaUpgrades: false,
    allowUsersToCopyApp: false,
    customSignInAppearance: true,
    quotas: {
        ...orgFreeFlags.quotas,
        [QuotaKind.RowsUsed]: { prepaidUsage: 100, maxOverage: 0 },
    },
};

export const eminencePro: EminenceFlags = {
    ...proEminenceFlags,
    eminence: Eminence.Pro,
    displayName: "Pro App",
    badge: "Pro",
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: false,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    upgradeButton: false,
    canUpgrade: false,
    canDowngrade: true,
    canTransferToOrg: true,
    canUseSignInWithGoogle: false,
    downgradeBeforeDelete: true,
    downgradeBeforeDeleteStripeCancellation: "immediately",
    logOrgLogin: false,
    allowUsersToCopyApp: true,
    canSubmitTemplate: true,
    needsPublishableOrgToTransfer: true,
    transferToOrgAs: "public",
    canHaveBoosts: false,
    roles: true,
};

// In lots of cases where we don’t have eminence flags,
// and we just want “eminence flags with everything turned on”.
// This use to be eminencePro, but it was missing some settings
// for app bootstraping like excel row quotas
export const eminenceFull: EminenceFlags = {
    ...eminencePro,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: true,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: true,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: true,
    },
    quotas: {
        ...eminencePro.quotas,
        [QuotaKind.ExcelRowsUsed]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
    },
};

export const eminenceOrgInternal: EminenceFlags = {
    ...proEminenceFlags,
    eminence: Eminence.OrgInternal,
    displayName: "Pro App (Legacy Organization Internal)",
    badge: undefined,
    authenticationSupport: {
        public: false,
        publicWithEmail: false,
        publicDisabled: false,
        password: false,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: true,
        justMe: false,
        optionalPublicWithEmail: false,
        optionalOtherEmail: false,
    },
    upgradeButton: false,
    canUpgrade: false,
    canDowngrade: false,
    canTransferToOrg: false,
    downgradeBeforeDelete: true,
    logOrgLogin: true,
    allowUsersToCopyApp: false,
    canSubmitTemplate: false,
    needsPublishableOrgToTransfer: false,
    transferToOrgAs: undefined,
    canHaveBoosts: false,
    roles: true,
};

export const eminenceOrgPublic: EminenceFlags = {
    ...proEminenceFlags,
    eminence: Eminence.OrgPublic,
    isFreeEminence: false,
    isV1Pro: true,
    displayName: "Pro App (Legacy Organization)",
    badge: "Pro",
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: false,
        justMe: false,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    upgradeButton: false,
    canUpgrade: false,
    canDowngrade: false,
    canTransferToOrg: false,
    canUnpublish: true,
    downgradeBeforeDelete: true,
    logOrgLogin: false,
    allowUsersToCopyApp: false,
    canSubmitTemplate: false,
    needsPublishableOrgToTransfer: false,
    transferToOrgAs: undefined,
    canHaveBoosts: false,
    roles: false,
    quotas: {
        ...proEminenceFlags.quotas,
        [QuotaKind.DeliverEmail]: { prepaidUsage: 0, maxOverage: 0 },
    },
};

export const unlimitedEminenceFlags: EminenceFlags = {
    eminence: Eminence.Pro,
    badge: "Enterprise",
    displayName: "Enterprise Unlimited",
    isFreeEminence: false,
    isV1Pro: false,
    upgradeButton: false,
    canUpgrade: false,
    canDowngrade: false,
    canTransferToOrg: false,
    logOrgLogin: false,
    allowUsersToCopyApp: false,
    canSubmitTemplate: false,
    needsPublishableOrgToTransfer: true,
    transferToOrgAs: undefined,
    canHaveBoosts: false,
    roles: true,
    confirmChangeAuthOnUpgrade: false,
    needsBuilderModifyAppFeatures: false,
    removeBranding: true,
    inYourFaceBranding: false,
    lowTransactionFees: true,
    googleSharedDrive: true,
    customDomains: true,
    withGrandfathering: false,
    automaticRefresh: true,
    googleAnalytics: true,
    isUserMetered: false,
    unlimitedZapier: true,
    signInAgreements: true,
    customSignInAppearance: true,
    proComponents: true,
    proIcons: true,
    canDisableShare: true,
    canTransferBetweenUsers: false,
    canUnpublish: false,
    canPausePublish: true,
    canUseSignInWithGoogle: false,
    downgradeBeforeDelete: false,
    downgradeBeforeDeleteStripeCancellation: undefined,
    buyButton: true,
    showQuotaBanners: false,
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    proDisplayQuotas: false,
    tabletMode: true,
    showReportApp: false,
    protectedColumns: true,
    allowRealEmails: true,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 2,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    appLoginsSheet: true,
    supportsQuotaUpgrades: true,
    maxSingleUploadBytes: 1024 * 1024 * 1024,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: true,
        justMe: false,
        optionalPublicWithEmail: true,
        optionalOtherEmail: true,
    },
    quotas: {
        [QuotaKind.BarcodesScanned]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.DeliverEmail]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.ExcelRowsUsed]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.FileBytesUsed]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.Geocodes]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.MapPins]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.MutateTablesAPI]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.PrivateUsers]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.Reloads]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.RowsUsed]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.Signatures]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
        [QuotaKind.ZapsAndWebhook]: { prepaidUsage: maxQuotaNumber, maxOverage: 0 },
    },
};

// This enforces app features based on eminence.  There's a somewhat similar
// function `adaptAppToEminence` which is used to set features to new defaults
// when an app's eminence changes.  We're using this in
// `AppInfoFetcher.getAppFeatures`, so it's used on the backend when getting
// app features from the builder app, too.
export function enforceEminenceInAppFeatures(
    {
        removeBranding,
        withGrandfathering,
        googleAnalytics,
        allowUsersToCopyApp,
        canDisableShare,
        automaticRefresh,
        tabletMode,
        customSignInAppearance,
        isPrivateProTrial,
        allowRealEmails: allowRealEmailsFromEminence,
        allowVirtualEmails,
    }: EminenceFlags,
    features: AppFeatures,
    grandfatheredFeatures: Partial<GrandfatheringFeatures>,
    daysLeftInTrial: number | undefined
): AppFeatures {
    const trialExpired = daysLeftInTrial !== undefined && daysLeftInTrial <= 0;
    const allowRealEmails = allowRealEmailsFromEminence || grandfatheredFeatures.allowRealEmails === true;
    features = {
        ...features,
        removeBranding,
        useCustomSignInBackgroundImageInLoading: customSignInAppearance
            ? features.useCustomSignInBackgroundImageInLoading ?? false
            : false,
        allowTabletMode: tabletMode,
        googleAnalyticsMeasureIDs: googleAnalytics ? features.googleAnalyticsMeasureIDs : [],
        enableShareScreen: canDisableShare ? features.enableShareScreen === true : true,
        disableSharing: canDisableShare ? features.disableSharing : false,
        isWorldCopyable: allowUsersToCopyApp && features.isWorldCopyable === true,
        automaticRefreshMinutes: automaticRefresh ? features.automaticRefreshMinutes : undefined,
        defaultTabletMode: tabletMode ? features.defaultTabletMode : false,
        isTrialLocked: isPrivateProTrial && trialExpired,
        virtualEmailAddresses: !allowRealEmails ? true : !allowVirtualEmails ? false : features.virtualEmailAddresses,
    };
    if (withGrandfathering) {
        features = { ...features, ...grandfatheredFeatures };
    }
    return features;
}

export function getBaseRowQuota(appFeatures: NonUserAppFeatures | undefined): number | undefined;
export function getBaseRowQuota(appFeatures: NonUserAppFeatures | undefined, flags: EminenceFlags): number;
export function getBaseRowQuota(
    appFeatures: NonUserAppFeatures | undefined,
    flags?: EminenceFlags
): number | undefined {
    if (appFeatures?.noRowLimit === true) {
        return proEminenceFlags.quotas[QuotaKind.RowsUsed].prepaidUsage;
    }
    return flags?.quotas[QuotaKind.RowsUsed].prepaidUsage ?? defaultQuotaLimits[QuotaKind.RowsUsed].prepaidUsage;
}
